import { filesAPI } from '../api/files';

interface ProcessingCallbacks {
  onStatusUpdate?: (status: string, currentStep?: string) => void;
  onComplete?: (results: any) => void;
  onError?: (error: string) => void;
}

class ProcessingService {
  private pollingIntervals = new Map<string, NodeJS.Timeout>();

  /**
   * Start processing a file
   */
  async startProcessing(fileId: string, callbacks?: ProcessingCallbacks): Promise<void> {
    try {
      console.log(`[ProcessingService] Starting processing for file ${fileId}`);
      const response = await filesAPI.processFile(fileId);

      if (response.success) {
        console.log(`[ProcessingService] Processing started successfully for file ${fileId}`);
        // Start polling for status updates
        this.pollProcessingStatus(fileId, callbacks);
      } else {
        throw new Error(response.message || 'Failed to start processing');
      }
    } catch (error: any) {
      console.error(`[ProcessingService] Failed to start processing for file ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Poll processing status with automatic cleanup
   */
  pollProcessingStatus(
    fileId: string,
    callbacks?: ProcessingCallbacks,
    pollInterval: number = 5000
  ): void {
    // Clear any existing polling for this file
    this.stopPolling(fileId);

    console.log(`[ProcessingService] Starting status polling for file ${fileId}`);

    const interval = setInterval(async () => {
      try {
        const response = await filesAPI.getProcessingStatus(fileId);

        if (!response.success) {
          console.error(`[ProcessingService] Failed to get status for file ${fileId}:`, response.message);
          callbacks?.onError?.(response.message || 'Failed to get processing status');
          this.stopPolling(fileId);
          return;
        }

        const { status, currentStep } = response.data!;

        // Update UI with current status
        callbacks?.onStatusUpdate?.(status, currentStep);

        if (status === 'completed') {
          console.log(`[ProcessingService] Processing completed for file ${fileId}`);
          this.stopPolling(fileId);
          // Fetch and display results
          await this.fetchResults(fileId, callbacks);
        } else if (status === 'failed') {
          console.log(`[ProcessingService] Processing failed for file ${fileId}`);
          this.stopPolling(fileId);
          callbacks?.onError?.('Processing failed');
        }
      } catch (error: any) {
        console.error(`[ProcessingService] Error polling status for file ${fileId}:`, error);
        callbacks?.onError?.(error.message || 'Failed to poll processing status');
        this.stopPolling(fileId);
      }
    }, pollInterval);

    this.pollingIntervals.set(fileId, interval);
  }

  /**
   * Fetch processing results
   */
  async fetchResults(fileId: string, callbacks?: ProcessingCallbacks): Promise<void> {
    try {
      console.log(`[ProcessingService] Fetching results for file ${fileId}`);
      const response = await filesAPI.getLLMResults(fileId);

      if (response.success && response.data?.results) {
        console.log(`[ProcessingService] Results fetched successfully for file ${fileId}`);
        callbacks?.onComplete?.(response.data.results);
      } else {
        throw new Error(response.message || 'Failed to fetch results');
      }
    } catch (error: any) {
      console.error(`[ProcessingService] Failed to fetch results for file ${fileId}:`, error);
      callbacks?.onError?.(error.message || 'Failed to fetch processing results');
    }
  }

  /**
   * Stop polling for a specific file
   */
  stopPolling(fileId: string): void {
    const interval = this.pollingIntervals.get(fileId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(fileId);
      console.log(`[ProcessingService] Stopped polling for file ${fileId}`);
    }
  }

  /**
   * Stop all polling
   */
  stopAllPolling(): void {
    for (const [fileId, interval] of this.pollingIntervals) {
      clearInterval(interval);
      console.log(`[ProcessingService] Stopped polling for file ${fileId}`);
    }
    this.pollingIntervals.clear();
  }
}

// Export singleton instance
export const processingService = new ProcessingService();